import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import * as Sentry from '@sentry/react';

import { useLocation } from 'js/lib/useRouter';

import {
  encodeSsoSamlMagicLink,
  getMagicLinkType,
  getSsoLoginByToken,
} from 'bundles/authentication-common/components/actions';
import { InvalidDomainSSOError } from 'bundles/authentication-common/components/sso/SSOError';
import type SSOError from 'bundles/authentication-common/components/sso/SSOError';

type UseInvalidDomainErrorAttributes = {
  setSsoError: Dispatch<SetStateAction<SSOError | undefined>>;
  setIsAuthLoading: Dispatch<SetStateAction<boolean>>;
  checkIsSSOLockedOut: (email: string) => Promise<boolean>;
  navigateBasedOnLoginMethods: (email: string) => Promise<void>;
};

/**
 * When the URL indicates an invalid-domain auth error with a magic-link token,
 * resolves SSO lockout and login methods, then surfaces {@link InvalidDomainSSOError} state.
 */
const useHandleInvalidDomainError = ({
  setSsoError,
  setIsAuthLoading,
  checkIsSSOLockedOut,
  navigateBasedOnLoginMethods,
}: UseInvalidDomainErrorAttributes) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tokenFromSearch = searchParams.get('token') || '';
  const isInvalidDomainAuthError = searchParams.get('authError') === 'invalidDomain';

  useEffect(() => {
    if (!isInvalidDomainAuthError) {
      setSsoError((prev) => (prev instanceof InvalidDomainSSOError ? undefined : prev));
      setIsAuthLoading(false);
    } else if (!tokenFromSearch) {
      setSsoError(new InvalidDomainSSOError({}));
      setIsAuthLoading(false);
    } else {
      (async () => {
        try {
          setIsAuthLoading(true);
          // Step 1: Fetch login details from token
          const ssoLoginResponse = await getSsoLoginByToken(tokenFromSearch);

          if (!ssoLoginResponse?.email) {
            setSsoError(new InvalidDomainSSOError({}));
            return;
          }
          const { email } = ssoLoginResponse;

          // If the token is invalid, the ssoLogin response itself
          // will be invalid and hence control will never reach here.
          // Hence, it is safe to assume that the tokenFromSearch
          // is of the format METHOD~token.
          const tokenWithoutMethod = tokenFromSearch.split('~')[1];
          const showResendLink =
            ssoLoginResponse.action === 'LOGIN_WITH_SAML_MAGICLINK' ||
            ssoLoginResponse.action === 'VERIFY_ACCOUNT_WITH_MAGIC_LINK';

          // Step 1.5: Send a request to trigger magic link email.
          if (showResendLink) {
            // No need to wait for this async call.
            // fire & forget.
            encodeSsoSamlMagicLink({
              samlLoginId: tokenWithoutMethod || '',
              magicLinkType: getMagicLinkType(ssoLoginResponse.action),
            });
          }

          // Step 2: Fetch sso-locked out state
          const isSSOLockedOut = await checkIsSSOLockedOut(email);

          // Step 3: Switch to login / signup screen based on user's
          // login methods if the user is not sso-locked out user.
          if (isSSOLockedOut) {
            setSsoError(
              new InvalidDomainSSOError({
                isSSOLockedOut,
                token: tokenWithoutMethod,
                ssoLoginResponse,
                showResendLink,
                ...(showResendLink ? { prefillEmail: email } : {}),
              })
            );
          } else {
            await navigateBasedOnLoginMethods(email);
            setSsoError(
              new InvalidDomainSSOError({
                ssoLoginResponse,
                showResendLink,
                isSSOLockedOut,
                prefillEmail: email,
                token: tokenWithoutMethod,
              })
            );
          }
        } catch (error) {
          setSsoError(new InvalidDomainSSOError({}));
          Sentry.captureException(error, {
            extra: {
              message: (error as { responseJSON?: { msg?: string } })?.responseJSON?.msg,
            },
          });
        } finally {
          setIsAuthLoading(false);
        }
      })();
    }

    // All function dependencies are intentionally excluded; only re-run when
    // invalid-domain flag or token query param (parsed) changes, not when unrelated query string changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvalidDomainAuthError, tokenFromSearch]);
};

export default useHandleInvalidDomainError;
